
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest, ApiError } from '../../utils/apiClient';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import RoleBadge from '../../components/team/RoleBadge';
import type { TeamRole } from '../../types/company';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import { latnLocale } from '../../utils/numberFormat';

interface InvitationDetails {
  id: string;
  companyId: string;
  companyName: string;
  email: string;
  role: string;
  invitedByName: string;
  expiresAt: string;
  existingUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    listingCount: number;
  } | null;
}

interface AcceptResponse {
  token: string;
  refreshToken?: string;
  expiresAt?: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    role?: string;
    [key: string]: unknown;
  };
  message: string;
  listingsTransferred?: number;
}

interface FormData {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  transferListings: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

export default function JoinTeamPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    transferListings: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch invitation details
  const fetchInvitation = useCallback(async () => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    try {
      const data = await apiRequest<InvitationDetails>(`/companies/invitation/${token}`);
      setInvitation(data);

      // Pre-fill form if existing user
      if (data.existingUser) {
        setFormData(prev => ({
          ...prev,
          firstName: data.existingUser!.firstName,
          lastName: data.existingUser!.lastName,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch invitation:', err);
      const message = err instanceof ApiError ? err.message : 'Failed to load invitation';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // For new users, validate name and password
    if (!invitation?.existingUser) {
      if (!formData.firstName.trim()) {
        newErrors.firstName = t('auth.firstNameRequired');
      } else if (formData.firstName.trim().length < 2) {
        newErrors.firstName = t('auth.firstNameTooShort');
      }

      if (!formData.lastName.trim()) {
        newErrors.lastName = t('auth.lastNameRequired');
      } else if (formData.lastName.trim().length < 2) {
        newErrors.lastName = t('auth.lastNameTooShort');
      }

      if (!formData.password) {
        newErrors.password = t('auth.passwordRequired');
      } else if (formData.password.length < 8) {
        newErrors.password = t('auth.passwordTooShort');
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        newErrors.password = t('auth.passwordWeak');
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t('auth.confirmPasswordRequired');
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('auth.passwordsMismatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !token) return;

    setSubmitting(true);
    setErrors({});

    try {
      const payload: Record<string, unknown> = {};

      if (invitation?.existingUser) {
        // Existing user - just need to decide about listings
        payload.transferListings = formData.transferListings;
      } else {
        // New user - need all account details
        payload.firstName = formData.firstName.trim();
        payload.lastName = formData.lastName.trim();
        payload.password = formData.password;
      }

      const response = await apiRequest<AcceptResponse>(
        `/companies/invitation/${token}/accept`,
        { method: 'POST', body: payload }
      );

      setSuccess(true);

      // Only auto-login for NEW users (when tokens are provided)
      // Existing users stay logged in as themselves - no session change
      if (response.token && response.refreshToken) {
        const SESSION_STORAGE_KEY = 'safaqat24.auth.session';
        const session = {
          token: response.token,
          refreshToken: response.refreshToken,
          expiresAt: response.expiresAt,
          user: response.user,
        };
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
      }

      // Redirect after a brief delay
      setTimeout(() => {
        navigate('/', { replace: true });
        window.location.reload(); // Refresh to update company membership
      }, 2000);

    } catch (err) {
      console.error('Accept invitation error:', err);
      const message = err instanceof ApiError ? err.message : 'Failed to accept invitation';
      setErrors({ general: message });
    } finally {
      setSubmitting(false);
    }
  };

  // Format expiry date
  const formatExpiryDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(latnLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check if invitation is expired
  const isExpired = invitation ? new Date(invitation.expiresAt) < new Date() : false;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20">
          <div className="text-center">
            <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
            <p className="text-gray-600">Loading invitation details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-3xl text-red-600"></i>
              </div>
              <h2 className="text-xl font-bold text-[var(--pv-color-primary)] mb-2">
                Invalid Invitation
              </h2>
              <p className="text-gray-600 mb-6">
                {error || 'This invitation link is invalid or has expired.'}
              </p>
              <Link to="/">
                <Button>
                  <i className="ri-home-line mr-2"></i>
                  Go to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Expired invitation
  if (isExpired) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-time-line text-3xl text-amber-600"></i>
              </div>
              <h2 className="text-xl font-bold text-[var(--pv-color-primary)] mb-2">
                Invitation Expired
              </h2>
              <p className="text-gray-600 mb-4">
                This invitation expired on {formatExpiryDate(invitation.expiresAt)}.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Please contact {invitation.invitedByName} at {invitation.companyName} for a new invitation.
              </p>
              <Link to="/">
                <Button>
                  <i className="ri-home-line mr-2"></i>
                  Go to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-xl font-bold text-[var(--pv-color-primary)] mb-2">
                Welcome to {invitation.companyName}!
              </h2>
              <p className="text-gray-600 mb-4">
                You have successfully joined the team. Redirecting you to the home page...
              </p>
              <div className="flex justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-[#004D40]"></i>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-16 sm:pt-20 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-[var(--pv-color-surface-sunken)] flex items-center justify-center">
              <i className="ri-team-line text-3xl text-[#004D40]"></i>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--pv-color-primary)] mb-2">
              Join {invitation.companyName}
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              {invitation.invitedByName} has invited you to join their team
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 sm:p-8">
            {/* Invitation Info */}
            <div className="mb-6 p-4 bg-[var(--pv-color-surface-sunken)] border border-[var(--pv-border-soft)] rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-building-line text-[#004D40]"></i>
                <span className="font-medium text-gray-900">{invitation.companyName}</span>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-mail-line text-[#004D40]"></i>
                <span className="text-gray-700">{invitation.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-user-star-line text-[#004D40]"></i>
                <span className="text-gray-700">Role:</span>
                <RoleBadge role={invitation.role.toUpperCase() as TeamRole} size="md" />
              </div>
              <p className="text-xs text-gray-500 mt-1 mr-6">
                This role was assigned by the inviter and cannot be changed
              </p>
            </div>

            {errors.general && (
              <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-md">
                <i className="ri-error-warning-line mr-2"></i>
                {errors.general}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {invitation.existingUser ? (
                /* Existing User Flow */
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <i className="ri-user-line text-blue-600"></i>
                      <span className="font-medium text-gray-900">
                        Welcome back, {invitation.existingUser.firstName}!
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      You already have an account. By accepting this invitation,
                      you will join {invitation.companyName} as{' '}
                      <RoleBadge role={invitation.role.toUpperCase() as TeamRole} size="sm" />.
                    </p>
                  </div>

                  {invitation.existingUser.listingCount > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
                      <div className="flex items-start gap-3">
                        <i className="ri-home-line text-amber-600 mt-0.5"></i>
                        <div>
                          <p className="font-medium text-gray-900 mb-1">
                            You have {invitation.existingUser.listingCount} existing listing{invitation.existingUser.listingCount !== 1 ? 's' : ''}
                          </p>
                          <p className="text-sm text-gray-600 mb-3">
                            Would you like to transfer your listings to {invitation.companyName}?
                          </p>
                          <div className="space-y-2">
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="radio"
                                name="transferListings"
                                checked={!formData.transferListings}
                                onChange={() => handleInputChange('transferListings', false)}
                                className="h-4 w-4 text-[#004D40]"
                              />
                              <span className="text-sm text-gray-700">
                                Keep my listings personal
                              </span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="radio"
                                name="transferListings"
                                checked={formData.transferListings}
                                onChange={() => handleInputChange('transferListings', true)}
                                className="h-4 w-4 text-[#004D40]"
                              />
                              <span className="text-sm text-gray-700">
                                Transfer all listings to {invitation.companyName}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* New User Flow */
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 text-center mb-4">
                    Create your account to join {invitation.companyName}
                  </p>

                  {/* Name Fields */}
                  <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        First Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Enter first name"
                        className={errors.firstName ? 'border-red-300' : ''}
                        disabled={submitting}
                        autoComplete="given-name"
                      />
                      {errors.firstName && (
                        <p className="text-red-600 text-xs mt-1">{errors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Last Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Enter last name"
                        className={errors.lastName ? 'border-red-300' : ''}
                        disabled={submitting}
                        autoComplete="family-name"
                      />
                      {errors.lastName && (
                        <p className="text-red-600 text-xs mt-1">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  {/* Email (read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email
                    </label>
                    <Input
                      type="email"
                      value={invitation.email}
                      disabled
                      className="bg-gray-50"
                    />
                    <p className="text-gray-500 text-xs mt-1">
                      This email was used for the invitation
                    </p>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Password *
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="Create a password"
                        className={`pr-12 ${errors.password ? 'border-red-300' : ''}`}
                        disabled={submitting}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer p-2"
                      >
                        <i className={`text-lg ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-600 text-xs mt-1">{errors.password}</p>
                    )}
                    <p className="text-gray-500 text-xs mt-1">
                      {t('auth.passwordRequirements')}
                    </p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        placeholder="Confirm your password"
                        className={`pr-12 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                        disabled={submitting}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer p-2"
                      >
                        <i className={`text-lg ${showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-600 text-xs mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Joining team...
                  </>
                ) : (
                  <>
                    <i className="ri-team-line mr-2"></i>
                    {invitation.existingUser ? 'Accept Invitation' : 'Create Account & Join'}
                  </>
                )}
              </Button>
            </form>

            {/* Expiry notice */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                This invitation expires on {formatExpiryDate(invitation.expiresAt)}
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/"
              className="text-sm text-gray-600 hover:text-[#004D40] transition-colors flex items-center justify-center"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
